import type {CopilotUIMessage} from '@mixpanel/ai-copilot/agent/types';
import type {CopilotToolSet} from '@mixpanel/ai-copilot/agent/tools';
import type {SparkMetricToolOutput} from '@mixpanel/ai-copilot/agent/tools/metrics/types';
import {isDashboardHit, isEventHit, isMetricHit, isReportHit} from '@mixpanel/ai-copilot/agent/tools/search/types';
import type {SearchEntityResponse} from '@mixpanel/ai-copilot/indexer/types';
import {getToolName, isStaticToolUIPart, isToolUIPart, type TextUIPart} from 'ai';

import {initMixpanel} from 'common/report/tracking-setup';
import {reporter} from 'common/report/error-reporting.ts';
import {isAsyncAgentEnabled} from 'common/report/util/feature-sampling';

import {ENTITY_REF_REGEX_GLOBAL} from './message/text/entity-refs';
import {getCopyableTextFromMessage} from './util';

export type CopilotSurface = `side_panel` | `full_page`;
function getCurrentChatSurface(): CopilotSurface {
  return /\/app\/agent(\/|$)/.test(window.location.pathname) ? `full_page` : `side_panel`;
}

export enum CopilotTrackingEvent {
  MessageThumbsUpClicked = `[Copilot] Message Thumbs Up Clicked`,
  MessageThumbsDownClicked = `[Copilot] Message Thumbs Down Clicked`,

  PromptSent = `[Copilot] Prompt Sent`,
  ResponseReceived = `[Copilot] Response Received`,
  SuggestionClicked = `[Copilot] Suggestion Clicked`,
  MessageStopClicked = `[Copilot] Message Stop Clicked`,

  SparkOpened = `[Copilot] Spark Opened`,
  SparkClosed = `[Copilot] Spark Closed`,
  ClearChatClicked = `[Copilot] Clear Chat Clicked`,
  ChatHistoryOpened = `[Copilot] Chat History Opened`,
  ConversationLoaded = `[Copilot] Conversation Loaded`,
  ConversationDeleted = `[Copilot] Conversation Deleted`,
  ConversationRenamed = `[Copilot] Conversation Renamed`,

  ShareModalOpened = `[Copilot] Share Modal Opened`,
  SharePrivacyToggled = `[Copilot] Share Privacy Toggled`,
  ShareLinkCopied = `[Copilot] Share Link Copied`,
  SharedChatVisited = `[Copilot] Shared Chat Visited`,
  SharedChatLoaded = `[Copilot] Shared Chat Loaded`,
  SharedChatLoadFailed = `[Copilot] Shared Chat Load Failed`,

  ErrorBoundaryTriggered = `[Copilot] Error Boundary Triggered`,
  ToolFailed = `[Copilot] Tool Failed`,
  ToolProcessingFailed = `[Copilot] Tool Processing Failed`,
  ResponseFailed = `[Copilot] Response Failed`,

  EntityClicked = `[Copilot] Entity Clicked`,

  AutoNavigated = `[Copilot] Auto-Navigated`,

  EditApprovalResponded = `[Copilot] Edit Approval Responded`,

  CreateReportToastClicked = `[Copilot] Create Report Toast Clicked`,
  CreateBoardToastClicked = `[Copilot] Create Board Toast Clicked`,

  GovernanceFilterApplied = `[Copilot] Governance Filter Applied`,
  NoVerifiedDataEmptyStateShown = `[Copilot] No Verified Data Empty State Shown`,

  AsyncAgentRouteUsed = `[Copilot] Async Agent Route Used`,
  StreamReconnected = `[Copilot] Stream Reconnected`,
  StreamResumeCompleted = `[Copilot] Stream Resume Completed`,
  HistoryItemClicked = `[Copilot] History Item Clicked`,
  StreamDisconnected = `[Copilot] Stream Disconnected`,
  TurnCancelCompleted = `[Copilot] Turn Cancel Completed`,
  ApprovalRequested = `[Copilot] Approval Requested`,
  QuestionAnswered = `[Copilot] Question Answered`,
  ToolInvoked = `[Copilot] Tool Invoked`,
}

export enum VerifiedAiModeTrackingEvent {
  Toggled = `[Verified AI Mode] Toggled`,
  ToggleAborted = `[Verified AI Mode] Toggle Aborted`,
}

// `has_verified_entity` is intentionally omitted — it requires a separate API
// fetch and is captured on the toggle event where the settings page already has it loaded.
type GovernanceContext = {
  verified_ai_mode: boolean;
};
let governanceContext: GovernanceContext | null = null;

export function setGovernanceContext(context: GovernanceContext): void {
  governanceContext = context;
}

export const getMessageTrackingData = (message: CopilotUIMessage) => {
  return {
    rawMessage: message, // Allow us to retroactively analyze message content if needed.
    messageId: message.id,
    messageRole: message.role,
    messageText: getCopyableTextFromMessage(message),
    spanId: message.metadata?.spanId,
    traceUrl: message.metadata?.traceUrl,
    session_id: message.metadata?.sessionId,
  };
};

// Only the option a caller actually needs today — sending on `pagehide`, where
// a normal XHR can be cancelled by the browser before it leaves the page.
type TrackOptions = {transport: `sendBeacon`};

let mixpanel: any | null;
export function track(
  event: CopilotTrackingEvent | VerifiedAiModeTrackingEvent,
  data: Record<string, any> = {},
  options?: TrackOptions,
) {
  // Tracking failures must never propagate into product code (e.g. test envs
  // where the mixpanel global is absent, or transient network errors).
  try {
    // Don't initialize mixpanel immediately on import so we don't break tests
    if (!mixpanel) {
      mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
    }
    const props = {
      chat_surface: getCurrentChatSurface(),
      ...governanceContext,
      // Read at event time so mid-session flag flips still split async vs legacy.
      async_agent: isAsyncAgentEnabled(),
      ...data,
    };
    // Omit the third arg entirely when unset, rather than passing `undefined` —
    // mixpanel-browser's `track(event, props, optionsOrCallback)` treats a present
    // third arg as an options object even when undefined, which changes its
    // internal branching versus a true 2-arg call.
    if (options) {
      mixpanel.track(event, props, options);
    } else {
      mixpanel.track(event, props);
    }
  } catch (error) {
    reporter.error(error, `[Copilot] track failed for event: ${event}`);
  }
}

export function trackPromptSent(
  promptText: string,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
  contextMetadata?: {
    current_page_context_type?: string;
    board_card_count?: number;
  },
  rawMessage?: CopilotUIMessage,
) {
  const data: Record<string, any> = {
    prompt_text: promptText,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
    timestamp: Date.now(),
  };

  if (contextMetadata?.current_page_context_type) {
    data.current_page_context_type = contextMetadata.current_page_context_type;
  }
  if (contextMetadata?.board_card_count !== undefined) {
    data.board_card_count = contextMetadata.board_card_count;
  }
  if (rawMessage) {
    data.rawMessage = rawMessage;
  }

  track(CopilotTrackingEvent.PromptSent, data);
}

export function trackResponseReceived(
  responseText: string,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
  toolsUsed?: string[],
  rawMessage?: CopilotUIMessage,
  roundtripTimeMs?: number,
  entities?: CopilotEntityTrackingProps[],
  spanId?: string,
  promptMessageId?: string,
) {
  track(CopilotTrackingEvent.ResponseReceived, {
    response_text: responseText,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
    // The triggering user message's id — joins 1:1 to PromptSent.message_id.
    ...(promptMessageId ? {prompt_message_id: promptMessageId} : {}),
    ...(spanId ? {agent_span_id: spanId} : {}),
    tools_used: toolsUsed,
    tools_count: toolsUsed?.length || 0,
    timestamp: Date.now(),
    ...(rawMessage ? {rawMessage} : {}),
    ...(roundtripTimeMs !== undefined ? {roundtrip_time_ms: roundtripTimeMs} : {}),
    ...(entities && entities.length > 0 ? {entities} : {}),
  });
}

// Toolset keys (see @mixpanel/ai-copilot/agent/tools) we fire a dedicated
// `[Copilot] Tool Invoked` event for when they appear in a completed assistant
// turn. Filter on `tool_name` at query time. Add a key here to start tracking a tool.
export const TRACKED_INVOCATION_TOOLS = [
  `generateBusinessPriorities`,
  `liveEvents`,
  `createTrackingPlan`,
  `createImplementationSnippet`,
  `getProjectToken`,
] as const;

export function trackToolInvoked(
  toolName: string,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
  spanId?: string,
) {
  track(CopilotTrackingEvent.ToolInvoked, {
    tool_name: toolName,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
    ...(spanId ? {agent_span_id: spanId} : {}),
  });
}

export function getToolsUsedFromMessage(message: CopilotUIMessage): string[] {
  try {
    if (!message.parts) return [];
    const tools: string[] = [];
    for (const part of message.parts) {
      if (isToolUIPart(part)) {
        tools.push(getToolName(part));
      }
    }
    return tools;
  } catch (error) {
    track(CopilotTrackingEvent.ToolProcessingFailed, {
      error_message: error instanceof Error ? error.message : String(error),
      function_name: `getToolsUsedFromMessage`,
      message_id: message.id,
    });
    return [];
  }
}

export function getToolFailuresFromMessage(message: CopilotUIMessage): Array<{toolName: string; error: any}> {
  try {
    if (!message.parts) return [];
    const failures: Array<{toolName: string; error: any}> = [];
    for (const part of message.parts) {
      if (isToolUIPart(part) && part.state === `output-error`) {
        failures.push({
          toolName: getToolName(part),
          error: part.output,
        });
      }
    }
    return failures;
  } catch (error) {
    track(CopilotTrackingEvent.ToolProcessingFailed, {
      error_message: error instanceof Error ? error.message : String(error),
      function_name: `getToolFailuresFromMessage`,
      message_id: message.id,
    });
    return [];
  }
}

export function trackToolFailed(
  toolName: string,
  error: any,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
  spanId?: string,
) {
  track(CopilotTrackingEvent.ToolFailed, {
    tool_name: toolName,
    error_message: error?.message || String(error),
    error_details: error,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
    ...(spanId ? {agent_span_id: spanId} : {}),
  });
}

// The async-agent gateway JSON-encodes `{message, code}` into the AI SDK error
// frame's `errorText` (the legacy usage-limit 429 body does the same trick with
// `limit_type`, see getErrorToastMessage in session-context.tsx). Only `message` is
// required to recognize the shape — `code` may be absent without invalidating a
// perfectly good message. Anything that doesn't parse, or has no string `message`
// (e.g. the legacy 429 shape), falls through to the raw string.
export function parseStreamError(raw: string): {message: string; code?: string} {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === `object` && typeof (parsed as {message?: unknown}).message === `string`) {
      const code = (parsed as {code?: unknown}).code;
      return {message: (parsed as {message: string}).message, code: typeof code === `string` ? code : undefined};
    }
  } catch {
    // Not JSON — plain error text, used as-is.
  }
  return {message: raw};
}

export function trackResponseFailed(
  errorMessage: string,
  messageNumber: number,
  sessionId?: string,
  options?: {
    roundtripTimeMs?: number;
    messageId?: string;
    spanId?: string;
    promptMessageId?: string;
    errorCode?: string;
  },
) {
  const {roundtripTimeMs, messageId, spanId, promptMessageId, errorCode} = options ?? {};
  track(CopilotTrackingEvent.ResponseFailed, {
    error_message: errorMessage,
    message_number: messageNumber,
    session_id: sessionId,
    ...(messageId ? {message_id: messageId} : {}),
    // The triggering user message's id — joins 1:1 to PromptSent.message_id.
    ...(promptMessageId ? {prompt_message_id: promptMessageId} : {}),
    ...(spanId ? {agent_span_id: spanId} : {}),
    timestamp: Date.now(),
    ...(roundtripTimeMs !== undefined ? {roundtrip_time_ms: roundtripTimeMs} : {}),
    ...(errorCode ? {error_code: errorCode} : {}),
  });
}

export function consumeRoundtripTime(
  messages: CopilotUIMessage[],
  messageSendTimes: Map<string, number>,
): number | undefined {
  const lastUserMessage = [...messages].reverse().find((m) => m.role === `user`);
  if (!lastUserMessage?.id) return undefined;
  const sendTime = messageSendTimes.get(lastUserMessage.id);
  if (!sendTime) return undefined;
  messageSendTimes.delete(lastUserMessage.id);
  return Date.now() - sendTime;
}

export function trackMessageStopClicked(
  messageNumber: number,
  sessionId?: string,
  messageId?: string,
  spanId?: string,
) {
  track(CopilotTrackingEvent.MessageStopClicked, {
    message_number: messageNumber,
    session_id: sessionId,
    ...(messageId ? {message_id: messageId} : {}),
    ...(spanId ? {agent_span_id: spanId} : {}),
  });
}

export function trackSparkOpened(chatSurface?: CopilotSurface) {
  track(CopilotTrackingEvent.SparkOpened, chatSurface ? {chat_surface: chatSurface} : {});
}

export function trackSparkClosed(chatSurface?: CopilotSurface) {
  track(CopilotTrackingEvent.SparkClosed, chatSurface ? {chat_surface: chatSurface} : {});
}

export function trackClearChatClicked(sessionId?: string) {
  track(CopilotTrackingEvent.ClearChatClicked, {
    session_id: sessionId,
  });
}

export function trackSuggestionClicked(suggestionText: string, sessionId: string) {
  track(CopilotTrackingEvent.SuggestionClicked, {
    suggestion_text: suggestionText,
    session_id: sessionId,
  });
}

// `menu` = side-panel ChatTitleMenu → Delete on the active chat;
// `list` = trash icon on a row in the history list (side panel or full page).
export type ConversationDeleteSource = `menu` | `list`;

export function trackChatHistoryOpened() {
  track(CopilotTrackingEvent.ChatHistoryOpened);
}

export function trackConversationLoaded(conversationId: string, lastTurnComplete: boolean) {
  track(CopilotTrackingEvent.ConversationLoaded, {
    conversation_id: conversationId,
    last_turn_complete: lastTurnComplete,
  });
}

export function trackConversationDeleted(conversationId: string, deleteSource: ConversationDeleteSource) {
  track(CopilotTrackingEvent.ConversationDeleted, {
    conversation_id: conversationId,
    delete_source: deleteSource,
  });
}

export function trackConversationRenamed(conversationId: string) {
  track(CopilotTrackingEvent.ConversationRenamed, {conversation_id: conversationId});
}

export function trackShareModalOpened(conversationId: string, sharedNumMessages: number, isShared: boolean) {
  track(CopilotTrackingEvent.ShareModalOpened, {
    conversation_id: conversationId,
    num_messages: sharedNumMessages,
    is_shared: isShared,
  });
}

export function trackSharePrivacyToggled({
  conversationId,
  sharedNumMessages,
  newValue,
  previousValue,
}: {
  conversationId: string;
  sharedNumMessages: number;
  newValue: boolean;
  previousValue: boolean;
}) {
  track(CopilotTrackingEvent.SharePrivacyToggled, {
    conversation_id: conversationId,
    num_messages: sharedNumMessages,
    new_value: newValue,
    previous_value: previousValue,
  });
}

export function trackShareLinkCopied(conversationId: string, sharedNumMessages: number, includeCurrentPage: boolean) {
  track(CopilotTrackingEvent.ShareLinkCopied, {
    conversation_id: conversationId,
    num_messages: sharedNumMessages,
    include_current_page: includeCurrentPage,
  });
}

export function trackSharedChatVisited(conversationId: string, sharedNumMessages?: number) {
  track(CopilotTrackingEvent.SharedChatVisited, {
    conversation_id: conversationId,
    num_messages: sharedNumMessages,
  });
}

export function trackSharedChatLoaded(conversationId: string, sharedNumMessages?: number) {
  track(CopilotTrackingEvent.SharedChatLoaded, {
    conversation_id: conversationId,
    num_messages: sharedNumMessages,
  });
}

export function trackSharedChatLoadFailed(
  conversationId: string,
  sharedNumMessages: number | undefined,
  errorMessage: string,
) {
  track(CopilotTrackingEvent.SharedChatLoadFailed, {
    conversation_id: conversationId,
    num_messages: sharedNumMessages,
    error_message: errorMessage,
  });
}

export enum CopilotEntityType {
  Cohort = `cohort`,
  Event = `event`,
  User = `user`,
  Property = `property`,
  SavedReport = `saved_report`,
  Dashboard = `dashboard`,
  SavedMetric = `saved_metric`,
  GeneratedChart = `generated_chart`,
  GeneratedMetric = `generated_metric`,
  Experiment = `experiment`,
  FeatureFlag = `feature_flag`,
  SessionReplayMoment = `session_replay_moment`,
  SessionReplayPlaylist = `session_replay_playlist`,
  Automation = `automation`,
}

export function trackEntityClicked(
  entityType: CopilotEntityType,
  entityName: string,
  extraProps: Record<string, unknown> = {},
) {
  track(CopilotTrackingEvent.EntityClicked, {
    entity_type: entityType,
    entity_name: entityName,
    ...extraProps,
  });
}

export function trackAutoNavigated(
  entityType: CopilotEntityType,
  entityName: string,
  extraProps: Record<string, unknown> = {},
) {
  track(CopilotTrackingEvent.AutoNavigated, {
    entity_type: entityType,
    entity_name: entityName,
    ...extraProps,
  });
}

type CopilotEntityTrackingProps = {
  entity_type: CopilotEntityType;
  entity_name: string;
};

const metricExtractor = (output: SparkMetricToolOutput): CopilotEntityTrackingProps[] =>
  output.metrics?.length
    ? [
        {
          entity_type: CopilotEntityType.GeneratedChart,
          entity_name: output.metrics.map((metric) => metric.type).join(`,`),
        },
      ]
    : [];

const queryBookmarkExtractor = (): CopilotEntityTrackingProps[] => [
  {entity_type: CopilotEntityType.GeneratedChart, entity_name: `saved-bookmark`},
];

type FlowsMetricOutput = CopilotToolSet[`flowsMetric`][`output`];
type CreateCohortOutput = CopilotToolSet[`createCohort`][`output`];
type CreateBoardToolOutput = CopilotToolSet[`createBoardTool`][`output`];
type SaveMetricOutput = CopilotToolSet[`saveMetric`][`output`];
type SearchQueryContextOutput = CopilotToolSet[`searchQueryContext`][`output`];
type SurfaceReplayMomentsOutput = CopilotToolSet[`surfaceReplayMoments`][`output`];

const flowsMetricExtractor = (output: FlowsMetricOutput): CopilotEntityTrackingProps[] =>
  output.metric ? [{entity_type: CopilotEntityType.GeneratedChart, entity_name: output.metric.type}] : [];

const createCohortExtractor = (output: CreateCohortOutput): CopilotEntityTrackingProps[] =>
  output.title ? [{entity_type: CopilotEntityType.Cohort, entity_name: output.title}] : [];

const createBoardToolExtractor = (output: CreateBoardToolOutput): CopilotEntityTrackingProps[] =>
  output.title ? [{entity_type: CopilotEntityType.Dashboard, entity_name: output.title}] : [];

const saveMetricExtractor = (output: SaveMetricOutput): CopilotEntityTrackingProps[] =>
  output.savedMetric?.name ? [{entity_type: CopilotEntityType.SavedMetric, entity_name: output.savedMetric.name}] : [];

// searchQueryContext returns a mix of events, metrics, reports, and dashboards. Mapping
// must stay 1:1 with `results` so citation indices (`[[N]]`) line up with the right entity at lookup time.
const searchQueryContextExtractor = (output: SearchQueryContextOutput): CopilotEntityTrackingProps[] => {
  if (!Array.isArray(output?.results)) return [];
  return output.results.map((hit) => {
    if (isEventHit(hit)) return {entity_type: CopilotEntityType.Event, entity_name: hit.value};
    if (isMetricHit(hit)) return {entity_type: CopilotEntityType.SavedMetric, entity_name: hit.name};
    if (isReportHit(hit)) return {entity_type: CopilotEntityType.SavedReport, entity_name: hit.name};
    if (isDashboardHit(hit)) return {entity_type: CopilotEntityType.Dashboard, entity_name: hit.name};

    hit satisfies never;
    // Unreachable per the union type — guards above exhaust SearchQuestionHitType.
    // Cast for safety if upstream ever returns an unexpected `collection`.
    return {entity_type: CopilotEntityType.Event, entity_name: (hit as {name?: string}).name ?? ``};
  });
};

const surfaceReplayMomentsExtractor = (output: SurfaceReplayMomentsOutput): CopilotEntityTrackingProps[] =>
  Array.isArray(output?.moments)
    ? output.moments.map((moment) => ({
        entity_type: CopilotEntityType.SessionReplayMoment,
        entity_name: moment.label,
      }))
    : [];

const searchExtractor = <F extends `value` | `name`>(
  entityType: CopilotEntityType,
  nameField: F,
): ((output: {results?: Array<SearchEntityResponse<Record<F, string>>>}) => CopilotEntityTrackingProps[]) => {
  return (output) => {
    const results = output?.results;
    if (!Array.isArray(results)) {
      return [];
    }
    return results.map((hit) => ({entity_type: entityType, entity_name: hit[nameField]}));
  };
};

type EntityExtractorMap = {
  [K in keyof CopilotToolSet]?: (output: CopilotToolSet[K][`output`]) => CopilotEntityTrackingProps[];
};

const ENTITY_EXTRACTORS: EntityExtractorMap = {
  searchEvents: searchExtractor(CopilotEntityType.Event, `value`),
  searchUserProperties: searchExtractor(CopilotEntityType.Property, `value`),
  searchEventProperties: searchExtractor(CopilotEntityType.Property, `value`),
  searchDashboards: searchExtractor(CopilotEntityType.Dashboard, `name`),
  searchMetrics: searchExtractor(CopilotEntityType.SavedMetric, `name`),
  searchExperiments: searchExtractor(CopilotEntityType.Experiment, `name`),
  searchFeatureFlags: searchExtractor(CopilotEntityType.FeatureFlag, `name`),
  metric: metricExtractor,
  queryBookmark: queryBookmarkExtractor,
  flowsMetric: flowsMetricExtractor,
  createCohort: createCohortExtractor,
  createBoardTool: createBoardToolExtractor,
  saveMetric: saveMetricExtractor,
  searchQueryContext: searchQueryContextExtractor,
  surfaceReplayMoments: surfaceReplayMomentsExtractor,
};

// Returns entities the agent surfaced in its final response.
//
// 1. Walk the message once and build a map keyed by the citation token format the
//    agent emits (`toolCallId:toolType:[[index]]`) → CopilotEntityTrackingProps.
//    Non-citable tools (e.g. `metric`, which renders a chart inline and isn't
//    referenced via [[N]]) get collected separately.
// 2. Scan the last text part for citation tokens and look each one up in the map.
// 3. Append the inline-rendered tool entities — the user always sees them in the
//    assistant message regardless of whether the agent mentions them in text.
export function getEntitiesFromMessage(message: CopilotUIMessage): CopilotEntityTrackingProps[] {
  try {
    if (!message.parts) return [];

    const lastTextPart = [...message.parts].reverse().find((part): part is TextUIPart => part.type === `text`);

    const entitiesByCitationKey = new Map<string, CopilotEntityTrackingProps>();
    const inlineEntities: CopilotEntityTrackingProps[] = [];

    // The agent will render at most one chart per final response. (message/index.tsx uses
    // findLast to pick a single tool part.
    //
    // NOTE: This is brittle and relies on duplicating how the message rendering works.
    //       A longer term pattern would be to register final entities in the React component
    //       and read those entities for tracking.
    let lastChartEntity: CopilotEntityTrackingProps[] | null = null;

    for (const part of message.parts) {
      if (!isStaticToolUIPart(part) || part.state !== `output-available` || !part.output) continue;

      const toolName = getToolName(part) as keyof CopilotToolSet;
      const extractor = ENTITY_EXTRACTORS[toolName] as
        | ((output: CopilotToolSet[keyof CopilotToolSet][`output`]) => CopilotEntityTrackingProps[])
        | undefined;
      if (!extractor) continue;

      const extracted = extractor(part.output as CopilotToolSet[keyof CopilotToolSet][`output`]);

      if (`results` in part.output && Array.isArray(part.output.results)) {
        // Search-style: each result is referenced by `toolCallId:toolName:[[N]]`
        // (1-indexed). The full match string from ENTITY_REF_REGEX_GLOBAL equals this
        // key, so token lookup is a direct map.get(match[0]).
        extracted.forEach((entity, i) => {
          entitiesByCitationKey.set(`${part.toolCallId}:${toolName}:[[${i + 1}]]`, entity);
        });
      } else if (toolName === `metric` || toolName === `flowsMetric`) {
        lastChartEntity = extracted;
      } else {
        inlineEntities.push(...extracted);
      }
    }

    if (lastChartEntity) {
      inlineEntities.push(...lastChartEntity);
    }

    const finalEntities: CopilotEntityTrackingProps[] = [];
    const seenEntityKeys = new Set<string>();

    const pushEntity = (entity: CopilotEntityTrackingProps) => {
      const entityKey = `${entity.entity_type}|${entity.entity_name}`;
      if (seenEntityKeys.has(entityKey)) return;

      seenEntityKeys.add(entityKey);
      finalEntities.push(entity);
    };

    if (lastTextPart?.text) {
      for (const match of lastTextPart.text.matchAll(ENTITY_REF_REGEX_GLOBAL)) {
        const entity = entitiesByCitationKey.get(match[0]);
        if (entity) pushEntity(entity);
      }
    }

    inlineEntities.forEach(pushEntity);

    return finalEntities;
  } catch (error) {
    reporter.error(error, `getEntitiesFromMessage failed`);
    return [];
  }
}

export type GovernanceFilterApplied = {
  toolName: string;
  filteredCount: number;
  filteredEntityTypes: string[];
  resultWasEmptyAfterFilter: boolean;
  // Total entities considered (filtered + still-allowed). Lets us compute
  // filter rate = filtered_count / total_count.
  totalCount?: number;
};

export function trackGovernanceFilterApplied(filter: GovernanceFilterApplied, messageId?: string, sessionId?: string) {
  track(CopilotTrackingEvent.GovernanceFilterApplied, {
    tool_name: filter.toolName,
    filtered_count: filter.filteredCount,
    filtered_entity_types: filter.filteredEntityTypes,
    result_was_empty_after_filter: filter.resultWasEmptyAfterFilter,
    total_count: filter.totalCount,
    message_id: messageId,
    session_id: sessionId,
  });
}

export function trackNoVerifiedDataEmptyStateShown(toolName: string, messageId?: string, sessionId?: string) {
  track(CopilotTrackingEvent.NoVerifiedDataEmptyStateShown, {
    tool_name: toolName,
    message_id: messageId,
    session_id: sessionId,
  });
}

export type AsyncAgentSendType = `user_prompt` | `interactive_resubmit`;

export function trackAsyncAgentRouteUsed(
  sessionId: string,
  messageNumber: number,
  sendType: AsyncAgentSendType,
  trigger?: string,
) {
  track(CopilotTrackingEvent.AsyncAgentRouteUsed, {
    session_id: sessionId,
    message_number: messageNumber,
    send_type: sendType,
    ...(trigger ? {trigger} : {}),
  });
}

export function trackHistoryItemClicked(conversationId: string, source: CopilotSurface) {
  track(CopilotTrackingEvent.HistoryItemClicked, {
    conversation_id: conversationId,
    source,
  });
}

export function trackStreamReconnected(
  conversationId: string,
  messageCount: number,
  {onPageReload}: {onPageReload: boolean},
) {
  track(CopilotTrackingEvent.StreamReconnected, {
    conversation_id: conversationId,
    message_count: messageCount,
    on_page_reload: onPageReload,
  });
}

export type StreamResumeOutcome = `replayed` | `nothing_pending` | `error`;

export type StreamResumeCompletedProps = {
  sessionId: string;
  outcome: StreamResumeOutcome;
  onPageReload: boolean;
  // The dangling user turn the resume reattached to — the unique-turns key.
  messageId?: string;
  httpStatus?: number;
};

// `replayed` means the resume attached to a live stream, not that the replay
// finished cleanly. Client aborts fire nothing — a cancel isn't an outcome.
export function trackStreamResumeCompleted(props: StreamResumeCompletedProps) {
  track(CopilotTrackingEvent.StreamResumeCompleted, {
    session_id: props.sessionId,
    outcome: props.outcome,
    on_page_reload: props.onPageReload,
    ...(props.messageId ? {message_id: props.messageId} : {}),
    ...(props.httpStatus !== undefined ? {http_status: props.httpStatus} : {}),
  });
}

// TurnCancelTrigger (not fresh literals) so a rename can't silently break the
// analytics join with `Turn Cancel Completed` — the same literal feeds both events.
export type StreamStopReason =
  | TurnCancelTrigger
  | `conversation_switched`
  | `clear_chat`
  | `project_switched`
  | `stream_error`;

// `page_unload` is deliberately NOT a StreamStopReason: it must never flow through
// `stopStream` (the pagehide path is track-only — no abort, bfcache-safe), and the
// split type lets the compiler enforce that instead of a comment.
export type StreamDisconnectReason = StreamStopReason | `page_unload`;

// Fires on every client-side teardown of an active stream — the in-flight check
// lives in session-context's `stopStream` choke point, so no teardown path can
// skip it. Intentional kills (`stop_button`, `thinking_timeout`) are included with
// their reason — filter them out at query time for "turn survived a disconnect" series.
// `messageId` is the in-flight turn's prompt (the last user message), the
// unique-turns key shared with `Turn Cancel Completed` / `Stream Resume Completed`.
export function trackStreamDisconnected(
  sessionId: string,
  reason: StreamDisconnectReason,
  messageId?: string,
  options?: TrackOptions,
) {
  track(
    CopilotTrackingEvent.StreamDisconnected,
    {
      session_id: sessionId,
      reason,
      ...(messageId ? {message_id: messageId} : {}),
    },
    options,
  );
}

export type TurnCancelOutcome = `accepted_202` | `no_active_turn_204` | `error`;
export type TurnCancelTrigger = `stop_button` | `thinking_timeout`;

// Async-agent-only: the cancel endpoint doesn't exist on the legacy path (a
// direct-path stop just aborts the browser's own fetch to Copilot).
export function trackTurnCancelCompleted(
  sessionId: string,
  outcome: TurnCancelOutcome,
  trigger: TurnCancelTrigger,
  httpStatus?: number,
) {
  track(CopilotTrackingEvent.TurnCancelCompleted, {
    session_id: sessionId,
    outcome,
    trigger,
    ...(httpStatus !== undefined ? {http_status: httpStatus} : {}),
  });
}

export type QuestionAnsweredType = `submitted` | `dismissed`;

export function trackQuestionAnswered(
  sessionId: string,
  messageId: string | undefined,
  questionCount: number,
  answerType: QuestionAnsweredType,
  counts: {answeredCount: number; skippedCount: number; customCount: number},
) {
  track(CopilotTrackingEvent.QuestionAnswered, {
    session_id: sessionId,
    message_id: messageId,
    question_count: questionCount,
    answer_type: answerType,
    answered_count: counts.answeredCount,
    skipped_count: counts.skippedCount,
    custom_count: counts.customCount,
  });
}

export type VerifiedAiModeToggleProps = {
  newValue: boolean;
  previousValue: boolean;
  confirmedViaModal: boolean;
  hasVerifiedEntity?: boolean;
};

export function trackVerifiedAiModeToggled(props: VerifiedAiModeToggleProps) {
  track(VerifiedAiModeTrackingEvent.Toggled, {
    new_value: props.newValue,
    previous_value: props.previousValue,
    confirmed_via_modal: props.confirmedViaModal,
    has_verified_entity: props.hasVerifiedEntity,
  });
}

export function trackVerifiedAiModeToggleAborted(props: {previousValue: boolean; hasVerifiedEntity?: boolean}) {
  track(VerifiedAiModeTrackingEvent.ToggleAborted, {
    previous_value: props.previousValue,
    has_verified_entity: props.hasVerifiedEntity,
  });
}
